import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Grid, MenuItem, Typography } from '@mui/material';
import { common } from '@mui/material/colors';
import { BaseSelectField } from 'common/components/base-select-field';
import FormProvider from 'common/components/form-provider';
import { colors, styleSelect } from 'common/constants';
import { skipQuestionSchema } from 'common/schema';
import React, { useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm, FieldErrors } from 'react-hook-form';
import { createStyleSheet, handleError } from 'utils/helpers';
import { ReactComponent as PlusIcon } from 'assets/images/icons/question-design/plus-question.svg';
import { ReactComponent as SaveIcon } from 'assets/images/icons/question-design/save-question.svg';
import CloseIcon from '@mui/icons-material/Close';
import { IQuestionLogicOptions, IQuestionLogicValues } from 'types/campaign';
import { saveSkipLogicOptions } from 'services/campaign.service';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AnswerOptionsFields from './answer_options';
import { IQuestionNewEditForm } from 'types/campaign-design';
import { listCampaignQuestionsFormatter } from 'utils/campaign_helpers';
import { setIsLoading } from 'slices/loadingSlice';
import { useAppDispatch } from 'common/hook';

interface LogicSkipQuestionProps {
    onClose: () => void;
    options: IQuestionLogicOptions;
    values: IQuestionLogicValues[];
    targetQuestionId: string | number | null;
    replaceQuestionByQuestionId: (
        questionId: number,
        question: IQuestionNewEditForm
    ) => void;
}
const LogicSkipQuestion: React.FC<LogicSkipQuestionProps> = ({
    onClose,
    options,
    values,
    targetQuestionId,
    replaceQuestionByQuestionId,
}) => {
    const questionOptions = useMemo(() => options?.questions || [], [options]);
    const conditionOptions = useMemo(
        () => options?.conditions || [],
        [options]
    );
    const currentCampaignId = Number(useParams().id);

    const [defaulLogic, setDefaultLogic] = useState<IQuestionLogicValues[]>([]);

    const methods = useForm({
        resolver: yupResolver(skipQuestionSchema),
        defaultValues: {
            logics: defaulLogic,
        },
    });

    const { handleSubmit, control, formState } = methods;
    const { append, fields } = useFieldArray({
        control,
        name: 'logics',
    });
    const dispatch = useAppDispatch();

    const onSubmit = handleSubmit((data) => {
        const saveData = async () => {
            try {
                dispatch(setIsLoading(true));
                const response = await saveSkipLogicOptions(
                    currentCampaignId,
                    Number(targetQuestionId),
                    data
                );
                if (response.success) {
                    toast.success(response.message);
                    replaceQuestionByQuestionId(
                        Number(targetQuestionId),
                        listCampaignQuestionsFormatter(response.data.question)
                    );
                    dispatch(setIsLoading(false));
                    onClose();
                } else {
                    dispatch(setIsLoading(false));
                    toast.error(response.message);
                }
            } catch (error) {
                dispatch(setIsLoading(false));
                handleError(error);
            }
        };
        saveData();
    });

    const errors1: FieldErrors<{
        logics: { answer: any; condition: number }[]; // eslint-disable-line
    }> = formState?.errors;

    useEffect(() => {
        const refreshOptions = () => {
            const newDefaultLogic = {
                question: Number(questionOptions[0]?.value),
                condition: 1,
                answer: 0,
            };
            setDefaultLogic([newDefaultLogic]);
            if (values.length) {
                methods.reset({
                    logics: values,
                });
            } else {
                methods.reset({
                    logics: [newDefaultLogic],
                });
            }
        };
        refreshOptions();
    }, [questionOptions, methods, values]);

    return (
        <>
            <Typography
                variant="h6"
                sx={{
                    color: common.black,
                    fontWeight: '600',
                    fontSize: '30px',
                }}
            >
                Skip Question
            </Typography>
            <Typography
                variant="body1"
                sx={{
                    color: common.black,
                    fontWeight: '400',
                    fontSize: '14px',
                    marginBottom: '16px',
                }}
            >
                Bỏ qua câu này nếu câu hỏi ... lựa chọn đáp án / các đáp án...
            </Typography>
            <FormProvider methods={methods} onSubmit={onSubmit}>
                {fields.map((item, index) => (
                    <Box
                        p={3}
                        key={index}
                        mb={2}
                        sx={{
                            backgroundColor: 'var(--color-primary-light-26)',
                            border: `2px dashed ${colors.main}`,
                        }}
                    >
                        <Grid container spacing={2} alignItems={'center'}>
                            <Grid item md={2}>
                                <Box
                                    sx={{
                                        fontSize: '24px',
                                        fontWeight: 600,
                                        color: common.black,
                                    }}
                                >
                                    Nếu
                                </Box>
                            </Grid>
                            <Grid item md={10}>
                                <Grid container spacing={2} mb={1}>
                                    <Grid item md={12}>
                                        <BaseSelectField
                                            name={`logics.${index}.question`}
                                            sx={{
                                                ...styleSelect,
                                                '& .MuiSelect-icon': {
                                                    right: '20px',
                                                    fontSize: '50px ',
                                                },
                                            }}
                                        >
                                            {questionOptions.map((option) => (
                                                <MenuItem
                                                    key={option.value}
                                                    value={option.value}
                                                >
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </BaseSelectField>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid container spacing={2}>
                            <Grid item md={2}></Grid>
                            <Grid item md={5}>
                                <BaseSelectField
                                    name={`logics.${index}.condition`}
                                    sx={{
                                        ...styleSelect,
                                        '& .MuiSelect-icon': {
                                            right: '20px',
                                            fontSize: '50px ',
                                        },
                                    }}
                                >
                                    {conditionOptions.map((option) => (
                                        <MenuItem
                                            key={option.value}
                                            value={option.value}
                                        >
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </BaseSelectField>
                            </Grid>
                            <Grid item md={5}>
                                <AnswerOptionsFields
                                    logicIndex={index}
                                    values={values}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                ))}
                {errors1.logics && (
                    <Typography
                        variant="body2"
                        sx={{ color: 'red', textAlign: 'center' }}
                    >
                        {errors1.logics?.root?.message}
                    </Typography>
                )}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mt: 2,
                    }}
                >
                    <Button
                        onClick={() => {
                            append(defaulLogic[0]);
                        }}
                        variant="contained"
                        sx={styles.button}
                        startIcon={<PlusIcon />}
                    >
                        Thêm điều kiện
                    </Button>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            sx={{
                                ...styles.button,
                                minWidth: '155px',
                                borderWidth: '2px',
                            }}
                            variant="outlined"
                            startIcon={<CloseIcon />}
                            onClick={onClose}
                        >
                            Đóng
                        </Button>
                        <Button
                            sx={{
                                ...styles.button,
                                minWidth: '155px',
                            }}
                            type="submit"
                            variant="contained"
                            startIcon={<SaveIcon />}
                        >
                            Lưu
                        </Button>
                    </Box>
                </Box>
            </FormProvider>
        </>
    );
};
const styles = createStyleSheet({
    button: {
        minWidth: '246px',
        minHeight: '60px',
        borderRadius: '40px',
        textTransform: 'none',
        fontSize: '20px',
        fontWeight: 700,
    },
});
export default LogicSkipQuestion;
